import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import toast from "react-hot-toast";
import { HiOutlineArrowLeft } from "react-icons/hi";
import * as Yup from "yup";
import {
  Loader,
} from "../../Components/Common";
import * as routes from "../../routes";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import {
  REFERRAL_ACTION_MODE,
  REFERRAL_STATUS_COLORS,
  REFERRAL_STATUS_LABELS,
  REFERRAL_VALIDATION_MESSAGES,
  TOAST_MESSAGES,
} from "./constants";
import { displayValue, formatAmount, formatDateDDMMYYYY, toProperCase } from "../../Utils";
import {
  createReferral,
  getReferralById,
  updateReferralById,
} from "../../Services/essServices";
import { UserContext } from "../../Context";
import { USER_ROLES } from "../../Constants/Strings";

const EmployeeReferralActions = () => {
  const { instanceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [mode, setMode] = useState(REFERRAL_ACTION_MODE.ADD);
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const isHROrAdmin = [USER_ROLES.HR, USER_ROLES.ADMIN].includes(user.role);
  const [currentStatus, setCurrentStatus] = useState("");
  const [hrRemark, setHrRemark] = useState("");
  const [remarkUpdating, setRemarkUpdating] = useState(false);
  const isHRRemarkEditable = [
    REFERRAL_STATUS_LABELS.REFERRED,
    REFERRAL_STATUS_LABELS.INTERVIEW_IN_PROGRESS,
  ].includes(currentStatus);

  const isViewMode = mode === REFERRAL_ACTION_MODE.VIEW;
  const filters = location.state?.filters || {
    status: "",
    referred_by: "",
    is_declaration_signed: "",
  };

  const fromPage = location.state?.fromPage || routes.EMP_REFERRAL_LIST;

  useEffect(() => {
    if (location.pathname.includes("view")) setMode(REFERRAL_ACTION_MODE.VIEW);
    else setMode(REFERRAL_ACTION_MODE.ADD);
  }, [location.pathname]);

  useEffect(() => {
    if (mode === REFERRAL_ACTION_MODE.VIEW) {
      const id = location.pathname.split("/").pop();
      setLoading(true);
      getReferralById(id)
        .then((res) => {
          setInitialValues(res.data);
          setCurrentStatus(res.data.status);
          const remark = res.data.hr_remark || "";
          setHrRemark(remark);
        })
        .catch(() => toast.error(TOAST_MESSAGES.FAILED_TO_FETCH_REFERRAL))
        .finally(() => setLoading(false));
    } else {
      setInitialValues({
        referred_by: "",
        referring_person_email: "",
        referring_person_mobile: "",
        referring_person_department: "",
        referred_person_name: "",
        referred_position: "",
        mobile_number: "",
        email: "",
        is_declaration_signed: false,
        status: "",
        resume: "",
        date_of_joining: "",
        relationship_with_candidate: "",
        hr_remark: "",
        created: "",
      });
    }
  }, [mode, location.pathname, currentStatus]);

  if (loading || !initialValues) {
    return (
      <div className="flex justify-center pt-10">
        <Loader size={4} />
      </div>
    );
  }

  const validationSchema = Yup.object().shape({
    referred_person_name: Yup.string().required(
      REFERRAL_VALIDATION_MESSAGES.referred_person_name
    ),
    referred_position: Yup.string().required(
      REFERRAL_VALIDATION_MESSAGES.referred_position
    ),
    mobile_number: Yup.string()
      .required(REFERRAL_VALIDATION_MESSAGES.mobile_number)
      .matches(/^\d{10}$/, "Enter a valid 10-digit mobile number"),
    email: Yup.string().required(REFERRAL_VALIDATION_MESSAGES.email),
    is_declaration_signed: Yup.boolean()
      .oneOf([true], "You must accept the declaration before submitting.")
      .required("You must accept the declaration before submitting."),

    resume: Yup.mixed()
      .required(REFERRAL_VALIDATION_MESSAGES.resume)
      .test(
        "fileRequired",
        REFERRAL_VALIDATION_MESSAGES.resume,
        (value) => value instanceof File
      ),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const formData = new FormData();
      formData.append("referred_person_name", values.referred_person_name);
      formData.append("referred_position", values.referred_position);
      formData.append("mobile_number", values.mobile_number);
      formData.append("email", values.email);
      formData.append("is_declaration_signed", values.is_declaration_signed);
      formData.append("resume", values.resume);
      formData.append(
        "relationship_with_candidate",
        values.relationship_with_candidate
      );

      const response = await createReferral(formData);

      if (response?.status === 201) {
        toast.success(TOAST_MESSAGES.ADD_REFERRAL);
        navigate(routes.EMP_REFERRAL_LIST);
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.FAILED_ADD_REFERRAL;
        toast.error(msg);
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.FAILED_ADD_REFERRAL;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };
  const handleStatusUpdate = async (referral_id, referral_status) => {
    try {
      setStatusUpdating(true); // start loading
      const response = await updateReferralById(referral_id, {
        status: referral_status,
      });
      if (response.status === 200) {
        toast.success(`Referral marked as ${toProperCase(referral_status)}`);
        setCurrentStatus(referral_status);
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.FAILED_TO_UPDATE;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || TOAST_MESSAGES.FAILED_TO_UPDATE;
      toast.error(msg);
    } finally {
      setStatusUpdating(false);
      if (
        referral_status ===
        (REFERRAL_STATUS_LABELS.REJECTED, REFERRAL_STATUS_LABELS.SELECTED)
      ) {
        navigate(routes.EMP_REFERRAL_LIST);
      }
    }
  };

  const handleHrRemarkSave = async () => {
    try {
      setRemarkUpdating(true);

      const response = await updateReferralById(instanceId, {
        hr_remark: hrRemark,
      });

      if (response.status === 200) {
        toast.success("HR remark updated successfully");
        // setIsRemarkLocked(true);
      } else {
        toast.error(TOAST_MESSAGES.FAILED_TO_UPDATE);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setRemarkUpdating(false);
    }
  };

  const handleBack = () => {
    navigate(fromPage, { state: { filters } });
  };

  if (loading || !initialValues)
    return (
      <div className="flex justify-center pt-10">
        <Loader size={4} />
      </div>
    );

  const commonInput =
    "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-secondary";

  return (
    <div
      className={`w-full  mx-auto ${isViewMode ? "w-full" : "max-w-xl shadow-md "
        } bg-white rounded-lg p-6 md:p-6`}
    >
      <div className="flex items-center justify-between mb-6">
        <button onClick={handleBack} className="text-primary text-2xl">
          <HiOutlineArrowLeft />
        </button>
        <h2
          className={`text-xl font-bold text-primary flex-1 ${!isViewMode ? "text-center" : ""
            }`}
        >
          {isViewMode ? "View Referral Details" : "Add Referral"}
        </h2>
        <div className="w-6" />
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={isViewMode ? null : validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form
            className={`grid ${isViewMode ? "grid-cols-1" : "grid-cols-1"}`}
          >
            {/* Add Mode */}
            {!isViewMode && (
              <div className="space-y-2">
                <div className="flex justify-between gap-4 w-full">
                  <div className="w-[50%]">
                    <label className="text-sm font-semibold text-secondary mb-1 block">
                      Referred Person
                      <sub className="text-red-500">{" * "}</sub>
                    </label>
                    <Field
                      type="text"
                      name="referred_person_name"
                      className={commonInput}
                      disabled={isSubmitting}
                      placeholder={"Enter name..."}
                    />
                    <ErrorMessage
                      name="referred_person_name"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                  <div className="w-[50%]">
                    <label className="text-sm font-semibold text-secondary mb-1 block">
                      Referred Position
                      <sub className="text-red-500">{" * "}</sub>
                    </label>
                    <Field
                      type="text"
                      name="referred_position"
                      className={commonInput}
                      disabled={isSubmitting}
                      placeholder={"Enter position..."}
                    />
                    <ErrorMessage
                      name="referred_position"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                </div>

                <div className="flex justify-between w-full gap-4">
                  {/* Mobile Number*/}
                  <div className="w-[50%]">
                    <label className="text-sm font-semibold text-secondary mb-1 block">
                      {"Mobile Number"}
                      <sub className="text-red-500">{" * "}</sub>
                    </label>
                    <Field
                      type="text"
                      name="mobile_number"
                      className={commonInput}
                      disabled={isSubmitting}
                      placeholder="Enter Mobile number..."
                      maxLength={10}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ""); // keep only digits
                        setFieldValue("mobile_number", value);
                      }}
                    />
                    <ErrorMessage
                      name="mobile_number"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                  <div className="w-[50%]">
                    <label className="text-sm font-semibold text-secondary mb-1 block">
                      {"Email"}
                      <sub className="text-red-500">{" * "}</sub>
                    </label>
                    <Field
                      type="text"
                      name="email"
                      className={commonInput}
                      disabled={isSubmitting}
                      placeholder={"Enter email..."}
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                </div>

                <div className="flex justify-between w-full gap-4">
                  <div className="w-[50%]">
                    <label className="text-sm font-semibold text-secondary mb-1 block">
                      {"Relationship With Candidate"}
                    </label>
                    <Field
                      type="text"
                      name="relationship_with_candidate"
                      className={commonInput}
                      disabled={isSubmitting}
                      placeholder={"Relationship With Candidate..."}
                    />
                  </div>
                  <div className="w-[50%]">
                    <label className="text-sm font-semibold text-secondary mb-1 block">
                      {"Resume"}
                      <sub className="text-red-500">{" * "}</sub>
                    </label>
                    <input
                      type="file"
                      name="resume"
                      className="w-full border border-gray-300 rounded-md text-sm focus:outline-none focus:border-secondary"
                      disabled={isSubmitting}
                      onChange={(event) => {
                        setFieldValue("resume", event.currentTarget.files[0]);
                      }}
                    />
                    <ErrorMessage
                      name="resume"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                </div>

                <div className="flex justify-between w-full">
                  <div className="w-full mt-4">
                    <span className="text-sm font-semibold text-secondary mb-1 block">
                      Declaration Sign
                    </span>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <Field
                        type="checkbox"
                        name="is_declaration_signed"
                        className="mt-1"
                      />
                      <span className="text-sm text-secondary leading-5 text-justify">
                        I confirm that all the information provided is true and
                        correct. I understand that the referral bonus of ₹5,000
                        will be awarded only if the referred candidate is hired,
                        joins the company, and successfully completes 3 months
                        of continuous employment.
                      </span>
                    </label>

                    <ErrorMessage
                      name="is_declaration_signed"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                </div>
              </div>
            )}
            {/* View Mode */}
            {isViewMode && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-[4fr_auto_2fr] gap-6">
                  {/* LEFT COLUMN (personal, education, emergency) */}
                  <div className="space-y-3">
                    {/* Personal Details */}
                    <div>
                      <h3 className="text-lg font-semibold text-primary mb-2 flex items-center gap-2">
                        <span className="inline-block w-1.5 h-5 bg-primary rounded-full" />
                        Referring Employee Details
                      </h3>
                      <div className="grid grid-cols-3 gap-2">
                        {/* First name */}
                        <div>
                          <label className="text-sm text-gray-700">
                            Referred By{" "}
                            <span className="text-red-500 top-0">*</span>
                          </label>
                          <p className="text-sm break-all">
                            {displayValue(initialValues.referred_by_full_name)}
                          </p>
                        </div>

                        {/* Last name */}
                        <div>
                          <label className="text-sm text-gray-700">
                            Department{" "}
                            <span className="text-red-500 top-0">*</span>
                          </label>
                          <p className="text-sm break-all">
                            {displayValue(
                              initialValues.referring_person_department
                            )}
                          </p>
                        </div>

                        {/* Email */}
                        <div>
                          <label className="text-sm text-gray-700">
                            Email <span className="text-red-500 top-0">*</span>
                          </label>
                          <p className="text-sm break-all">
                            {displayValue(initialValues.referring_person_email)}
                          </p>
                        </div>

                        {/* Mobile */}
                        <div>
                          <label className="text-sm text-gray-700">
                            Mobile Number{" "}
                            <span className="text-red-500 top-0">*</span>
                          </label>
                          <p className="text-sm break-all">
                            {displayValue(
                              initialValues.referring_person_mobile
                            )}
                          </p>
                        </div>

                        <div>
                          <label className="text-sm text-gray-700">
                            Relationship With Candidate
                          </label>
                          <p className="text-sm break-all">
                            {displayValue(
                              initialValues.relationship_with_candidate
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Candidate Details */}
                    <div>
                      <h3 className="text-lg font-semibold text-primary mb-2 flex items-center gap-2">
                        <span className="inline-block w-1.5 h-5 bg-primary rounded-full" />
                        Candidate (Referred Person) Details
                      </h3>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-sm text-gray-700">
                            Full Name{" "}
                            <span className="text-red-500 top-0">*</span>
                          </label>
                          <p className="text-sm break-all">
                            {initialValues.referred_person_name}
                          </p>
                        </div>

                        <div>
                          <label className="text-sm text-gray-700">
                            Position Applying For{" "}
                            <span className="text-red-500 top-0">*</span>
                          </label>
                          <p className="text-sm break-all">
                            {displayValue(initialValues.referred_position)}
                          </p>
                        </div>

                        <div>
                          <label className="text-sm text-gray-700">
                            Email <span className="text-red-500 top-0">*</span>
                          </label>
                          <p className="text-sm break-all">
                            {initialValues.email}
                          </p>
                        </div>

                        <div>
                          <label className="text-sm text-gray-700">
                            Mobile Number{" "}
                            <span className="text-red-500 top-0">*</span>
                          </label>
                          <p className="text-sm break-all">
                            {displayValue(initialValues.mobile_number)}
                          </p>
                        </div>

                        <div>
                          <label className="text-sm text-gray-700">
                            Date of Joining{" "}
                            <span className="text-red-500 top-0">*</span>
                          </label>
                          <p className="text-sm break-all">
                            {displayValue(
                              formatDateDDMMYYYY(initialValues.date_of_joining)
                            )}
                          </p>
                        </div>

                        <div>
                          <label className="text-sm font-semibold text-secondary">
                            Attached Resume{" "}
                            <span className="text-red-500 top-0">*</span>
                          </label>
                          <div className="w-full flex justify-between gap-4">
                            {initialValues.resume ? (
                              <a
                                href={initialValues.resume}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary underline"
                              >
                                View Resume
                              </a>
                            ) : (
                              <p className="text-gray-500 mt-1 text-sm break-all">
                                No resume uploaded
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* declaration */}
                    <div className="flex justify-between w-full mt-2 md:mt-0">
                      <div className="w-full">
                        <span className="text-sm font-semibold text-secondary mb-1 block">
                          Declaration Sign
                        </span>
                        <label className="flex items-start gap-2 cursor-pointer">
                          <Field
                            type="checkbox"
                            name="is_declaration_signed"
                            disabled={initialValues.is_declaration_signed}
                            className="mt-1 disabled:cursor-not-allowed disabled:text-gray-500"
                          />
                          <span className="text-sm text-secondary leading-5 text-justify">
                            I confirm that all the information provided is true
                            and correct. I understand that the referral bonus of
                            ₹5,000 will be awarded only if the referred
                            candidate is hired, joins the company, and
                            successfully completes 3 months of continuous
                            employment.
                          </span>
                        </label>

                        <ErrorMessage
                          name="is_declaration_signed"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>
                    </div>
                  </div>
                  {/* divider */}
                  <div className="w-[1px] bg-gray-400 md:mr-2 mt-2 hidden md:block"></div>
                  {/* RIGHT COLUMN referral status, hr remarks, salary slip */}
                  <div className="space-y-3">
                    {/* HR Use Only */}
                    <div className="md:mb-10">
                      <h3 className="text-lg font-semibold text-primary flex items-center gap-2 mb-3">
                        <span className="inline-block w-1.5 h-5 bg-primary rounded-full" />
                        HR Use Only
                      </h3>

                      <div className="grid grid-cols-2 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="text-sm text-gray-700">
                            Status <span className="text-red-500">*</span>
                          </label>
                          <p className="text-sm">
                            <span
                              className={`px-2 py-1 rounded-md text-xs font-medium ${REFERRAL_STATUS_COLORS[initialValues.status] ||
                                "bg-gray-100 text-gray-700"
                                }`}
                            >
                              {toProperCase(initialValues.status)}
                            </span>
                          </p>
                        </div>

                        <div>
                          <label className="text-sm text-gray-700">
                            Raised On
                          </label>
                          <p className="text-sm">
                            {formatDateDDMMYYYY(
                              initialValues.created.split("T")[0]
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-700">
                            HR Remark
                          </label>

                          {isHROrAdmin ? (
                            <textarea
                              rows={3}
                              value={hrRemark}
                              onChange={(e) => setHrRemark(e.target.value)}
                              disabled={!isHRRemarkEditable}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm
                     focus:outline-none focus:ring-1 focus:ring-primary
                     disabled:cursor-not-allowed"
                              placeholder="Enter HR remarks..."
                            />
                          ) : (
                            <p className="text-sm text-gray-800 whitespace-pre-line">
                              {displayValue(hrRemark)}
                            </p>
                          )}
                        </div>

                        {/* Save button column */}
                        {isHROrAdmin && isHRRemarkEditable && (
                          <div className="flex items-end">
                            <button
                              type="button"
                              onClick={handleHrRemarkSave}
                              disabled={remarkUpdating}
                              className="bg-primary hover:bg-secondary text-white px-4 py-1 font-medium rounded-md"
                            >
                              {remarkUpdating ? "Saving..." : "Save Remark"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Salary Slip Details */}
                    {isViewMode && (
                      <div className="mt-4 md:mt-6">
                        <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                          <span className="inline-block w-1.5 h-5 bg-primary rounded-full" />
                          Salary Slip Details
                        </h3>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-sm text-gray-700">
                              Salary Slip Date
                            </label>
                            <p className="text-sm break-all">
                              <span className="text-sm break-all">
                                {toProperCase(
                                  formatDateDDMMYYYY(
                                    initialValues.bonus_salary_slip?.start_date
                                  )
                                )}
                              </span>
                            </p>
                          </div>
                          <div>
                            <label className="text-sm text-gray-700">
                              Net Amount
                            </label>
                            <p className="text-sm">
                              {toProperCase(
                                formatAmount(
                                  initialValues.bonus_salary_slip?.net
                                )
                              )}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm text-gray-700">
                              Status
                            </label>
                            <p className="text-sm">
                              {toProperCase(
                                initialValues.bonus_salary_slip?.status
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* buttons reject & mark in progress */}
                    {isViewMode &&
                      isHROrAdmin &&
                      (currentStatus === REFERRAL_STATUS_LABELS.REFERRED ||
                        REFERRAL_STATUS_LABELS.INTERVIEW_IN_PROGRESS) && (
                        <div className="flex gap-3 mt-5 pt-4 justify-start">
                          {(currentStatus ===
                            REFERRAL_STATUS_LABELS.INTERVIEW_IN_PROGRESS ||
                            currentStatus ===
                            REFERRAL_STATUS_LABELS.REFERRED) && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleStatusUpdate(
                                    instanceId,
                                    REFERRAL_STATUS_LABELS.REJECTED
                                  )
                                }
                                className="bg-red-500 hover:bg-red-700 text-white px-4 py-1 font-medium rounded-md"
                                disabled={statusUpdating}
                              >
                                {statusUpdating ? "Updating..." : "Reject"}
                              </button>
                            )}
                          {currentStatus ===
                            REFERRAL_STATUS_LABELS.REFERRED && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleStatusUpdate(
                                    instanceId,
                                    REFERRAL_STATUS_LABELS.INTERVIEW_IN_PROGRESS
                                  )
                                }
                                className="bg-primary hover:bg-green-700 text-white px-4 py-1 font-medium rounded-md"
                                disabled={statusUpdating}
                              >
                                {statusUpdating
                                  ? "Updating..."
                                  : "Mark In Progress"}
                              </button>
                            )}
                          {currentStatus ===
                            REFERRAL_STATUS_LABELS.INTERVIEW_IN_PROGRESS && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleStatusUpdate(
                                    instanceId,
                                    REFERRAL_STATUS_LABELS.SELECTED
                                  )
                                }
                                className="bg-primary hover:bg-secondary text-white px-4 py-1 font-medium rounded-md"
                                disabled={statusUpdating}
                              >
                                {statusUpdating ? "Updating..." : "Select"}
                              </button>
                            )}
                        </div>
                      )}
                  </div>
                </div>
              </>
            )}
            {/* Buttons */}
            {!isViewMode && (
              <div className="flex justify-between gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => navigate(fromPage)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-lg font-semibold text-base"
                  disabled={isSubmitting}
                >
                  {"Cancel"}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primary hover:bg-secondary text-white px-3 py-1 rounded-lg"
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EmployeeReferralActions;